/**
 * Surface команды `task_view`.
 * Загружает задачу из payload и показывает её readonly-описание под subtitle
 * хоста (`ActivityContextView` уже рендерит title и "Кому ... до ..." сверху).
 *
 * Если задача не в финальном статусе — показываем кнопку «Завершить»,
 * которая открывает общую модалку `CompleteTaskModal` (там обязательный
 * комментарий и перевод задачи в FINISHED). Закрытие модалки — и при отмене,
 * и при успехе — закрывает Surface через `onSubmitted()`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Group, Loader, Stack, Text } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useTaskRepository } from '@ayocore/tasks/client/rpc';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import type { Task } from '@ayocore/tasks/schemas/task.schema';
import { extractActivityTaskId } from '@ayocore/activity/common/types/activity-command-context';
import { CompleteTaskModal } from '@ayocore/erp/crm/client/components/tasks/components/CompleteTaskModal';
import type { ActivitySlashCommandFormProps } from '@ayocore/activity/client/extensions';

/**
 * Surface для просмотра задачи в едином host (`ActivityContextView`).
 */
export function ActivityTaskViewCommandForm({
  context,
  onSubmitted,
}: ActivitySlashCommandFormProps) {
  const taskRepository = useTaskRepository();
  const completeModal = useModal(CompleteTaskModal);
  const taskId = useMemo(() => extractActivityTaskId(context.payload), [context.payload]);

  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const autoConfirm =
    context.payload && typeof context.payload === 'object'
      ? (context.payload as Record<string, unknown>).confirm_complete === true
      : false;

  useEffect(() => {
    if (!taskId) {
      setTask(null);
      setLoading(false);
      setErrorMessage('Не удалось определить задачу для просмотра.');
      return;
    }

    let disposed = false;
    setLoading(true);
    setErrorMessage(null);
    void (async () => {
      try {
        const loaded = await taskRepository.findById(taskId);
        if (disposed) return;
        if (!loaded) {
          setTask(null);
          setErrorMessage('Задача не найдена.');
          return;
        }
        setTask(loaded);
      } catch (error) {
        if (disposed) return;
        logger.error('[ActivityTaskViewCommandForm] Failed to load task', { taskId, error });
        setErrorMessage('Не удалось загрузить задачу.');
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    })();

    return () => {
      disposed = true;
    };
  }, [taskId, taskRepository]);

  const openConfirm = useCallback(
    (target: Task) => {
      // Закрытие модалки (отмена или после успеха) — закрывает Surface.
      completeModal.onClose(() => onSubmitted());
      completeModal.open(
        {
          task: target,
          onCompleted: () => completeModal.close(),
          onCancel: () => completeModal.close(),
          title: `Завершить задачу «#${target.id} ${target.title}»`,
        },
        { size: 'md' }
      );
    },
    [completeModal, onSubmitted]
  );

  // Авто-открытие модалки завершения, если флаг пришёл из payload пина —
  // юзер кликнул «Завершить» прямо в меню чипсы и не должен делать ещё один клик.
  useEffect(() => {
    if (autoConfirm && task && !task.task_status?.is_final) {
      openConfirm(task);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, autoConfirm]);

  if (loading) {
    return (
      <Group gap={6}>
        <Loader size={12} />
        <Text size='xs' c='dimmed'>
          Загрузка задачи...
        </Text>
      </Group>
    );
  }

  if (errorMessage) {
    return (
      <Alert color='red' p='xs' title='Ошибка'>
        {errorMessage}
      </Alert>
    );
  }

  const description = typeof task?.description === 'string' ? task.description.trim() : '';
  const canComplete = Boolean(task && !task.task_status?.is_final);

  return (
    <Stack gap={6}>
      {description ? (
        <Text size='xs' c='dimmed' style={{ whiteSpace: 'pre-wrap' }}>
          {description}
        </Text>
      ) : null}
      {canComplete && task ? (
        <Group justify='flex-end'>
          <Button size='xs' onClick={() => openConfirm(task)}>
            Завершить
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}
